import { PickupPointBookingQuestionTypeEnum } from '.'
import {
  Basket,
  ExperienceBasketItem,
  FREETEXT_UNIT,
  LOCATION_REFERENCE_UNIT,
  PICKUP_POINT_BOOKING_QUESTION_ID,
  SPECIAL_REQUIREMENTS_BOOKING_QUESTION_ID,
} from '../basket'
import { TravelerDetails } from '../form-data/traveler-details'

type RequiredDefined<T> = Required<Exclude<T, undefined>>

export const COLLECT_PRIMARY_DRIVER_EMAIL_ADDRESS = 'COLLECT_PRIMARY_DRIVER_EMAIL_ADDRESS' as const
export const COLLECT_PRIMARY_DRIVER_CONTACT_DETAILS =
  'COLLECT_PRIMARY_DRIVER_CONTACT_DETAILS' as const
export const COLLECT_MAIN_ACCOUNT_HOLDER_CONTACT_DETAILS =
  'COLLECT_MAIN_ACCOUNT_HOLDER_CONTACT_DETAILS' as const
export const COLLECT_NOTHING = 'COLLECT_NOTHING' as const

export type AccountHolderDataCollectionMethod =
  | typeof COLLECT_PRIMARY_DRIVER_EMAIL_ADDRESS
  | typeof COLLECT_PRIMARY_DRIVER_CONTACT_DETAILS
  | typeof COLLECT_MAIN_ACCOUNT_HOLDER_CONTACT_DETAILS
  | typeof COLLECT_NOTHING

const resolveHotelAdditionalDetails = (basket: Basket) => {
  const hotelBasketItem = basket.items.HOTEL?.at(0)
  if (!hotelBasketItem) {
    return undefined
  }

  return {
    name: hotelBasketItem.name,
    room: {
      count: hotelBasketItem.room.count,
      name: hotelBasketItem.room.name,
    },
    collectSpecialRequirements: true,
  }
}

export type HotelAdditionalDetailsContext = RequiredDefined<
  ReturnType<typeof resolveHotelAdditionalDetails>
>

const resolveCarHireAdditionalDetails = (basket: Basket) => {
  const hasCarHireBasketItem = Boolean(basket.items.CAR_HIRE?.length > 0)
  if (!hasCarHireBasketItem) {
    return undefined
  }

  return {
    collectAvisPreferredMembershipNumber: true,
    collectFlightNumber: true,
  }
}

export type CarHireAdditionalDetailsContext = RequiredDefined<
  ReturnType<typeof resolveCarHireAdditionalDetails>
>

export const getPickupPointBookingQuestionType = (
  experience: ExperienceBasketItem
): PickupPointBookingQuestionTypeEnum | undefined => {
  const pickupPointBookingQuestion = experience?.bookingQuestions.find(
    ({ id }) => id === PICKUP_POINT_BOOKING_QUESTION_ID
  )

  if (!pickupPointBookingQuestion) {
    return undefined
  }

  const hasLocationReferenceUnit =
    pickupPointBookingQuestion.units?.includes(LOCATION_REFERENCE_UNIT)

  const hasFreeTextUnit = pickupPointBookingQuestion.units?.includes(FREETEXT_UNIT)
  if (hasLocationReferenceUnit && hasFreeTextUnit) {
    return PickupPointBookingQuestionTypeEnum.LOCATION_REFERENCE_OR_FREETEXT
  }

  if (hasLocationReferenceUnit) {
    return PickupPointBookingQuestionTypeEnum.LOCATION_REFERENCE
  }

  return PickupPointBookingQuestionTypeEnum.FREETEXT
}

const resolveExperienceAdditionalDetails = (basket: Basket) => {
  const experience = basket.items.EXPERIENCE?.at(0)
  if (!experience) {
    return undefined
  }

  const pickupPointBookingQuestionType = getPickupPointBookingQuestionType(experience)
  const { pickupLocations } = experience

  const specialRequirements = experience.bookingQuestions.find(
    ({ id }) => id === SPECIAL_REQUIREMENTS_BOOKING_QUESTION_ID
  )

  return {
    pickupPointBookingQuestionType,
    pickupLocations,
    languageGuideOptions: experience.languageGuides,
    specialRequirements: specialRequirements && {
      maxLength: specialRequirements.maxLength,
    },
  }
}

export type ExperienceAdditionalDetailsContext = RequiredDefined<
  ReturnType<typeof resolveExperienceAdditionalDetails>
>

type ResolveAdditionalDetailsParams = {
  basket: Basket
  isFullAviosPayment: boolean
  travelerDetails?: TravelerDetails
}

type ResolveAccountHolderDataCollectionMethodParams = ResolveAdditionalDetailsParams

const resolveAccountHolderDataCollectionMethod = ({
  basket,
  isFullAviosPayment,
  travelerDetails,
}: ResolveAccountHolderDataCollectionMethodParams): AccountHolderDataCollectionMethod => {
  const hasCarHireBasketItem = Boolean(basket.items.CAR_HIRE?.length > 0)

  const isMainTravelerAccountHolder = Boolean(travelerDetails?.mainTraveler.isAccountHolder)

  if (isFullAviosPayment) {
    if (hasCarHireBasketItem && isMainTravelerAccountHolder) {
      return 'COLLECT_PRIMARY_DRIVER_EMAIL_ADDRESS'
    }
    if (hasCarHireBasketItem) {
      return 'COLLECT_PRIMARY_DRIVER_CONTACT_DETAILS'
    }
    if (!isMainTravelerAccountHolder) {
      return 'COLLECT_MAIN_ACCOUNT_HOLDER_CONTACT_DETAILS'
    }
  }

  if (hasCarHireBasketItem) {
    return 'COLLECT_PRIMARY_DRIVER_EMAIL_ADDRESS'
  }

  return 'COLLECT_NOTHING'
}

const resolveFlightAdditionalDetails = (basket: Basket) => {
  const flightBasketItem = basket.items.FLIGHT?.at(0)
  if (!flightBasketItem) {
    return undefined
  }

  return {
    vendorId: flightBasketItem.vendorId,
  }
}

export const resolveAdditionalDetailsContext = (params: ResolveAdditionalDetailsParams) => {
  const { basket } = params
  return {
    flight: resolveFlightAdditionalDetails(basket),
    hotel: resolveHotelAdditionalDetails(basket),
    carHire: resolveCarHireAdditionalDetails(basket),
    experience: resolveExperienceAdditionalDetails(basket),
    accountHolderDataCollectionMethod: resolveAccountHolderDataCollectionMethod(params),
  }
}

export type AdditionalDetailsContext = ReturnType<typeof resolveAdditionalDetailsContext>
